import { useMemo } from 'react';

// Imported from the enum's own file, NOT the `lib/modules` barrel. The barrel
// re-exports `use-module-routes`, which pulls in the user-state provider and
// the generated GraphQL types — a cycle that leaves `EnumUserRole` undefined at
// import time for anything that reaches this module first. `filterGameSpecies`
// is a pure function and its callers should not have to load half the app to
// get it (PRO-694 routed several species pickers through this barrel).
import { ModuleId } from 'lib/modules/module-id.enum';

import { filterGameSpecies } from './filter-game-species';
import { useUserState } from 'providers';

/**
 * Whether wild game is switched on for the processor the current user is
 * acting as.
 *
 * The ONE place the web app asks this question. Every game surface — the
 * drop-off action, the species pickers, the species setup — reads it, so
 * "wild game is off" cannot mean different things in different components.
 *
 * Fails CLOSED. An unloaded user, a user with no active vendor, or a vendor
 * with no module row all mean "off", so game never flashes into view while
 * state settles and then disappears.
 *
 * This is presentation only. The authoritative gate is server-side in
 * `createGameJob` — a hidden button is not a permission.
 * @returns true when the processor is in the wild-game limited release.
 */
export function useWildGameEnabled(): boolean {
  const { isActiveModule } = useUserState();
  return isActiveModule(ModuleId.WILD_GAME);
}

/**
 * A species list with wild game filtered out unless the processor is in the
 * release. Prefer this over reading a species constant directly.
 * @param species the full species list.
 * @returns the species the current user should be offered.
 */
export function useVisibleSpecies<T extends string>(
  species: readonly T[],
): T[] {
  const wildGameEnabled = useWildGameEnabled();
  return useMemo(
    () => filterGameSpecies(species, wildGameEnabled),
    [species, wildGameEnabled],
  );
}
